import { FC, ReactNode } from 'react';

import {
  AUTH_HIGHLIGHTS,
  PRIME_CC_URL,
  SHOW_AUTH_BRAND_PANEL,
  TERMS_OF_USE_URL,
} from '../../constants/Auth';

interface IAuthShellProps {
  heading: string;
  subheading?: string;
  children: ReactNode;
}

const FooterLinks: FC<{ tone: 'light' | 'dark' }> = ({ tone }) => {
  const muted = tone === 'light' ? 'text-[#B3CAE4]' : 'text-[#697586]';
  const hover = tone === 'light' ? 'hover:text-white' : 'hover:text-[#0C447C]';

  return (
    <div className={['flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px]', muted].join(' ')}>
      <span>© {new Date().getFullYear()} All rights reserved.</span>
      <a
        href={PRIME_CC_URL}
        target="_blank"
        rel="noreferrer"
        className={['underline transition-colors', hover].join(' ')}
      >
        PrimeCC
      </a>
      <span aria-hidden="true">·</span>
      <a
        href={TERMS_OF_USE_URL}
        target="_blank"
        rel="noreferrer"
        className={['underline transition-colors', hover].join(' ')}
      >
        Terms of Use
      </a>
    </div>
  );
};

/**
 * Shared chrome for the login and register screens: a branded panel on large
 * screens and a centred card beside it. The old pages stacked a logo and a bare
 * form on plain white, so the panel is the main upgrade — it gives the product
 * somewhere to speak and stops the form floating in empty space.
 */
const AuthShell: FC<IAuthShellProps> = ({ heading, subheading, children }) => (
  <div className="flex min-h-screen w-full bg-white" data-testid="auth-shell">
    {SHOW_AUTH_BRAND_PANEL && (
      <aside className="hidden w-full max-w-[440px] shrink-0 flex-col justify-between bg-primecc-navy p-10 lg:flex">
        <img
          src="/prime_logo_vertical.png"
          alt="PrimeCC Logo"
          width={200}
          className="self-start object-contain"
        />

        <div className="flex flex-col gap-8">
          <h2 className="text-[26px] font-semibold leading-snug text-white">
            Compliance documents,
            <br />
            <span className="text-[#95C147]">without the paperwork.</span>
          </h2>
          <ul className="flex flex-col gap-4">
            {AUTH_HIGHLIGHTS.map((highlight) => (
              <li key={highlight.label} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-white/10">
                  {/* <LocalIcon iconUrl={highlight.icon} className="h-4 w-4 bg-[#95C147]" /> */}
                </span>
                <span className="text-sm leading-relaxed text-[#B3CAE4]">{highlight.label}</span>
              </li>
            ))}
          </ul>
        </div>

        <FooterLinks tone="light" />
      </aside>
    )}

    <main className="flex flex-1 flex-col items-center px-6 pb-12">
      <div className="mt-20 flex w-full max-w-[400px] flex-col gap-8">
        <div className="flex flex-col items-center gap-4 text-center">
          <a href="/">
            <img
              src="/prime_logo_vertical.png"
              alt="PrimeCC Logo"
              width={200}
              className={[
                'h-auto max-w-36 object-contain',
                SHOW_AUTH_BRAND_PANEL ? 'lg:hidden' : '',
              ].join(' ')}
            />
          </a>
          <h2 className="text-primecc-green font-semibold text-3xl">Prime Documents</h2>
          <div className="flex flex-col gap-1">
            <h1 className="text-xl font-semibold text-[#121926]">{heading}</h1>
            <p className="text-sm text-[#4B5565]">{subheading}</p>
          </div>
        </div>

        {children}

        <div
          className={['flex justify-center', SHOW_AUTH_BRAND_PANEL ? 'lg:hidden' : ''].join(' ')}
        >
          <FooterLinks tone="dark" />
        </div>
      </div>
    </main>
  </div>
);

export default AuthShell;
